import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const SESSION_STORAGE_KEY = 'sectionsFeatureFlag';

interface FeatureFlagState {
    isROneFeatureEnabled: boolean;
    isSectionsModeEnabled: boolean;
    isInitialized: boolean;
}

const initialState: FeatureFlagState = {
    isROneFeatureEnabled: false,
    isSectionsModeEnabled: false,
    isInitialized: false,
};

// Session storage utilities
const getSessionStorageFlag = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    } catch (error) {
        console.warn('Could not access feature flag from sessionStorage', error);
        return false;
    }
};

const setSessionStorageFlag = (flagValue: boolean): void => {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, flagValue.toString());
    } catch (error) {
        console.warn('Could not save feature flag to sessionStorage', error);
    }
};

const featureFlagSlice = createSlice({
    name: 'featureFlag',
    initialState,
    reducers: {
        initializeFeatureFlag: (state, action: PayloadAction<{ urlParam?: string }>) => {
            const { urlParam } = action.payload;

            let flagValue = false;

            // Priority: URL param presence > sessionStorage
            if (urlParam) {
                flagValue = true;
            } else {
                flagValue = getSessionStorageFlag();
            }

            state.isROneFeatureEnabled = flagValue;
            state.isSectionsModeEnabled = flagValue;
            state.isInitialized = true;

            // Persist to sessionStorage
            setSessionStorageFlag(flagValue);
        },

        setFeatureEnabled: (state, action: PayloadAction<boolean>) => {
            state.isROneFeatureEnabled = action.payload;

            // If disabling feature, also disable sections mode
            if (!action.payload) {
                state.isSectionsModeEnabled = false;
            }

            // Persist to sessionStorage
            setSessionStorageFlag(action.payload);
        },

        setSectionsModeEnabled: (state, action: PayloadAction<boolean>) => {
            // Only allow if feature is enabled
            if (state.isROneFeatureEnabled) {
                state.isSectionsModeEnabled = action.payload;
            }
        },
    },
});

export const { initializeFeatureFlag, setFeatureEnabled, setSectionsModeEnabled } =
    featureFlagSlice.actions;

export const selectIsROneFeatureEnabled = (state: { featureFlag: FeatureFlagState }) =>
    state.featureFlag.isROneFeatureEnabled;

export const selectIsSectionsModeEnabled = (state: { featureFlag: FeatureFlagState }) =>
    state.featureFlag.isSectionsModeEnabled;

export const selectIsROneFlagInitialized = (state: { featureFlag: FeatureFlagState }) =>
    state.featureFlag.isInitialized;

export default featureFlagSlice.reducer;
